import { useNavigate, useParams } from 'react-router';

import { List, styled } from '@mui/material';

import { Section, Skeleton, Button } from '@app-velocity/ui';

import { FrameIcon } from '@app-velocity/icons';

import { useI18n } from '@app-velocity/i18n';

import { useCurrentTeamPermissions } from '@app-velocity/libs/team-permissions';

import { useGetAppraisalsQuery } from '../../queries/useGetAppraisalsQuery';
import { NoAppraisals } from './components/NoAppraisals/NoAppraisals';
import { useDeleteAppraisalMutation } from '../../queries/useDeleteAppraisalMutation';
import { AppraisalListContext } from './context/AppraisalListContext';

import { SingleAppraisal } from './components/SingleAppraisal';
import { AppraisalsRoutePaths } from '../../routes/AppraisalsRoutePaths';

const ReviewList = styled(List)(({ theme }) => ({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const AppraisalsSection = styled(Section)({
  flex: '0.9 0 0%',
});

export type ScreenAppraisalsProps = {
  fileId?: string;
};

export const ScreenAppraisals = ({ fileId }: ScreenAppraisalsProps) => {
  const { teamId, workspaceId } = useParams<{
    teamId: string;
    workspaceId: string;
  }>();
  const { t } = useI18n();
  const { data: appraisals, isLoading: isLoadingAllAppraisals } =
    useGetAppraisalsQuery(workspaceId, fileId);
  const navigate = useNavigate();
  const currentTeam = useCurrentTeamPermissions();

  const { mutate: removeAppraisal, isPending } =
    useDeleteAppraisalMutation(workspaceId);

  const addNewReview = () => {
    if (!workspaceId || !teamId || !currentTeam) return;

    if (!currentTeam.canRunAppraisals()) {
      return;
    }

    navigate(AppraisalsRoutePaths.reviewAPage(teamId, workspaceId));
  };

  const isLoading = isLoadingAllAppraisals || isPending;

  if (isLoading)
    return (
      <AppraisalsSection>
        <Skeleton height={53} borderRadius={1} margin="0" />
        <Skeleton height={53} borderRadius={1} margin="0" />
        <Skeleton height={53} borderRadius={1} margin="0" />
      </AppraisalsSection>
    );
  if (!appraisals || appraisals.length === 0)
    return (
      <AppraisalsSection>
        <NoAppraisals />
      </AppraisalsSection>
    );

  return (
    <AppraisalsSection
      header={
        <Section.StickyHeader
          title="appraisals:design_reviews"
          right={
            <Button
              data-ph-capture-attribute-id="create_review_button"
              onClick={addNewReview}
              variant="basic"
              color="primary"
              size="small"
            >
              {t('appraisals:review_a_page')}
              <FrameIcon />
            </Button>
          }
        />
      }
    >
      <AppraisalListContext.Provider value={{ removeAppraisal }}>
        <ReviewList disablePadding dense>
          {appraisals?.map((appraisal) => (
            <SingleAppraisal key={appraisal.id} appraisal={appraisal} />
          ))}
        </ReviewList>
      </AppraisalListContext.Provider>
    </AppraisalsSection>
  );
};
